import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Card } from "react-bootstrap";
import { useLocation, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBuilding, faUser } from "@fortawesome/free-solid-svg-icons";
import axios from "helpers/api";
import paths from "routes/paths";
import CommonBreadCrumb from "components/commonbreadcrumb/CommonBreadCrumb";
import PatientStock from "./PatientStock";
import OverdueAudits from "./OverdueAudits";

const authHeaders = () => {
  const tokenStr = localStorage.getItem("accessToken");
  return tokenStr ? { Authorization: `Bearer ${tokenStr}` } : {};
};

const apiUrl = (path) => {
  const baseUrl = process.env.REACT_APP_SERVER_URL || "";
  return baseUrl ? `${baseUrl}${path}` : path;
};

// Facilities come from localStorage "facilitiesData": [{ key, value, group_id, ... }]
const readFacilities = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem("facilitiesData"));
    const list = Array.isArray(parsed) ? parsed : Object.values(parsed || {});
    return list.filter((f) => f && f.value !== undefined && f.value !== null);
  } catch (e) {
    return [];
  }
};

const patientLabel = (p) => [p.last_name, p.first_name].filter(Boolean).join(", ").trim();

/* Placeholder shown when there is nothing to list. */
const EmptyState = ({ icon, title, text }) => (
  <div className="text-center py-5 px-3 border border-300 border-dashed rounded-3">
    <span
      className="d-inline-flex align-items-center justify-content-center rounded-circle bg-body-tertiary text-500 mb-3"
      style={{ width: 56, height: 56 }}
    >
      <FontAwesomeIcon icon={icon} style={{ fontSize: 22 }} />
    </span>
    <h6 className="text-900 fw-bold mb-1 fs-9">{title}</h6>
    <p className="text-600 fs-10 mb-0">{text}</p>
  </div>
);

EmptyState.propTypes = {
  icon: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.string
};

/**
 * Name and facility for the patient header. They come with the link from the
 * patient list; when the page is opened straight from its URL they are
 * fetched with GET /patients/get-patient/:id, as Patient Details does.
 */
const usePatientHeader = (patientId, facilityOptions) => {
  const { state } = useLocation();
  const linkedName = state?.patientName || "";
  const linkedFacility = state?.facilityName || "";
  const [header, setHeader] = useState({ name: linkedName, facilityName: linkedFacility });

  useEffect(() => {
    setHeader({ name: linkedName, facilityName: linkedFacility });
    if (!patientId || linkedName) return undefined;

    let cancelled = false;
    axios
      .get(apiUrl(`/patients/get-patient/${patientId}`), { headers: authHeaders() })
      .then((res) => {
        const p = res.data?.patients?.data;
        if (cancelled || !p) return;
        const facility = facilityOptions.find((f) => String(f.value) === String(p.facility_id));
        setHeader({ name: patientLabel(p), facilityName: facility?.key || "" });
      })
      .catch((err) => console.warn("Patient fetch warning:", err?.message));

    return () => {
      cancelled = true;
    };
  }, [patientId, linkedName, linkedFacility, facilityOptions]);

  return header;
};

/**
 * Inventory page.
 *   /inventory      the patient list (overdue audits for the whole group)
 *   /inventory/:id  that patient's tracked medications in a searchable table
 *                   (click one for its stock history)
 * Patient names link to /inventory/:id, the same pattern View Patient uses
 * for patient details.
 *
 * For now there are no tabs: the Delivery count, Periodic audit (counting)
 * and Audit history screens are not shown. Their components
 * (PatientInventory, PatientAudit, AuditHistory) are kept so they can be
 * switched back on.
 */
const InventoryPage = () => {
  const { id: patientId } = useParams();
  const facilityOptions = useMemo(readFacilities, []);
  const header = usePatientHeader(patientId, facilityOptions);

  const groupId =
    facilityOptions[0]?.group_id ||
    localStorage.getItem("groupId") ||
    localStorage.getItem("group_id") ||
    null;

  // On a patient, "Inventory" links back to the patient list.
  const breadcrumbItems = patientId
    ? [
        { name: "Dashboard", href: "/main", isActive: false },
        { name: "Inventory", href: paths.inventory, isActive: false },
        { name: header.name || "Patient", href: "#", isActive: true }
      ]
    : [
        { name: "Dashboard", href: "/main", isActive: false },
        { name: "Inventory", href: "#", isActive: true }
      ];

  return (
    <>
      <CommonBreadCrumb breadcrumbItems={breadcrumbItems} />

      <Card className="p-3">
        <h4 className="mb-3">Inventory</h4>

        {facilityOptions.length === 0 ? (
          <EmptyState
            icon={faBuilding}
            title="No facilities available"
            text="Your account has no facilities assigned, so there is no inventory to show."
          />
        ) : !patientId ? (
          // The overdue audit list for every facility in the group.
          <OverdueAudits groupId={groupId} />
        ) : (
          <>
            <div className="d-flex flex-wrap align-items-center gap-2 p-3 mb-3 rounded-3 border border-300">
              <FontAwesomeIcon icon={faUser} className="text-primary" />
              <span className="fw-bold text-900">{header.name || `Patient ${patientId}`}</span>
              {header.facilityName && (
                <>
                  <span className="text-500">·</span>
                  <span className="text-700 fs-10">{header.facilityName}</span>
                </>
              )}
            </div>

            <PatientStock key={patientId} patientId={patientId} />
          </>
        )}
      </Card>
    </>
  );
};

export default InventoryPage;
